import { IconButton, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SearchIcon from '@mui/icons-material/Search';
import Fuse from 'fuse.js';
import { useCallback, useEffect, useState } from 'react';

function fuzzySearch(rows, query, category, units, customFieldConfigs) {
  let fuseOptionsKeys;
  if (category === 'Journalists' || category === 'Community Members') {
    fuseOptionsKeys = ['name', 'Org.name', 'unitName', 'notes', 'Organization.name'];
  } else if (category === 'Experts') {
    fuseOptionsKeys = [
      'name',
      {
        name: 'unitNames',
        getFn: (expert) => {
          if (!expert.units || expert.units.length === 0) {
            return [];
          }
          return expert.units
            .map((unitId) => {
              const u = units.find((unit) => unit._id === unitId);
              return u ? u.name : null;
            })
            .filter(Boolean);
        },
      },
      'notes',
    ];
  } else {
    fuseOptionsKeys = ['name'];
  }

  if (customFieldConfigs) {
    fuseOptionsKeys = [...fuseOptionsKeys, ...customFieldConfigs.map((c) => c.fieldKey)];
  }

  const options = {
    threshold: 0.2,
    ignoreLocation: true,
    keys: fuseOptionsKeys,
  };
  const fuse = new Fuse(rows, options);
  const results = fuse.search(query).map((result) => result.item);
  return results;
}

const useStyles = makeStyles((theme) => ({
  contactFuzzySearchTextField: { width: '600px' },
  searchIcon: { backgroundColor: '#DEDEDE', borderRadius: '4px' },
}));

const ContactFuzzySearch = (props) => {
  const classes = useStyles();
  const { contacts, setSearchResults, category, units, customFieldConfigs, fullWidth, query, setQuery } = props;

  const [textFieldValue, setTextFieldValue] = useState('');
  const inputValue = textFieldValue;
  const [executedQuery, setExecutedQuery] = useState('');

  const getFuzzySearchPlaceHolder = () => {
    let customFieldLabels = '';
    if (customFieldConfigs && customFieldConfigs.length > 0) {
      customFieldLabels = `/${customFieldConfigs.map((c) => c.label).join('/')}`;
    }

    switch (category) {
      case 'Journalists':
        return `Name/Outlet/Notes${customFieldLabels}`;
      case 'Experts':
        return `Name/Unit/Notes${customFieldLabels}`;
      case 'Community Members':
        return `Name/Organization/Notes${customFieldLabels}`;
      case 'Organizations':
        return `Name${customFieldLabels}`;
      case 'Outlets':
        return `Name${customFieldLabels}`;
      default:
        return 'Search...';
    }
  };

  const handleSearch = useCallback(
    (keywords) => {
      if (keywords !== '') {
        const results = fuzzySearch(contacts, keywords, category, units, customFieldConfigs);
        const sortedResults = [...results].sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
        setSearchResults(sortedResults);
        setExecutedQuery(keywords);
        if (setQuery) {
          setQuery(keywords);
        }
      } else {
        const sortedContacts = [...contacts].sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
        setSearchResults(sortedContacts);
        setExecutedQuery('');
        if (setQuery) {
          setQuery('');
        }
      }
    },
    [contacts, category, setSearchResults, units, customFieldConfigs, setQuery]
  );

  const handleChange = (e) => {
    const nextValue = e.target.value;
    setTextFieldValue(nextValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(inputValue);
    }
  };

  const handleSearchButtonClick = (e) => {
    handleSearch(inputValue);
  };

  //reset search if category changed
  useEffect(() => {
    setTextFieldValue('');
    if (setQuery) {
      setQuery('');
    }
    setExecutedQuery('');
  }, [category, setQuery]);

  useEffect(() => {
    if (query === undefined) {
      return;
    }
    setTextFieldValue(query);
  }, [query]);

  useEffect(() => {
    if (!Array.isArray(contacts)) {
      return;
    }
    const areListsEqual = (listA, listB) => {
      if (listA.length !== listB.length) {
        return false;
      }
      for (let index = 0; index < listA.length; index += 1) {
        if (listA[index]?._id !== listB[index]?._id) {
          return false;
        }
      }
      return true;
    };

    if (executedQuery !== '') {
      const results = fuzzySearch(contacts, executedQuery, category, units, customFieldConfigs);
      const sortedResults = [...results].sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
      if (!areListsEqual(sortedResults, contacts)) {
        setSearchResults(sortedResults);
      }
      return;
    }
    const sortedContacts = [...contacts].sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
    if (!areListsEqual(sortedContacts, contacts)) {
      setSearchResults(sortedContacts);
    }
  }, [contacts, executedQuery, category, units, customFieldConfigs, setSearchResults]);

  return (
    <TextField
      variant="outlined"
      size="small"
      placeholder={getFuzzySearchPlaceHolder()}
      value={inputValue}
      onChange={handleChange}
      data-cy="fuzzySearch-input-ContactFuzzySearch"
      className={fullWidth ? undefined : classes.contactFuzzySearchTextField}
      fullWidth={fullWidth}
      onKeyDown={handleKeyPress}
      InputProps={{
        style: { paddingRight: 0 },
        endAdornment: (
          <IconButton onClick={handleSearchButtonClick} className={classes.searchIcon}>
            <SearchIcon />
          </IconButton>
        ),
      }}
    />
  );
};

export default ContactFuzzySearch;
